import React, { CSSProperties } from "react";
import "./Skeleton.css";

type CSSDimension = string | number;
type SkeletonTone = "theme" | "soft" | "neutral" | "alt" | "contrast";

export type LineChartSkeletonProps = {
  width?: CSSDimension;
  height?: CSSDimension;
  tone?: SkeletonTone;
  className?: string;
  style?: CSSProperties;
  seriesCount?: number;
  showLegend?: boolean;
  showAxes?: boolean;
};

function toCssDimension(value?: CSSDimension): string | undefined {
  if (typeof value === "number") {
    return `${value}px`;
  }

  return value;
}

const desktopSeriesPaths = [
  "M 40 188 C 95 152, 146 162, 202 126 S 302 108, 360 88",
  "M 40 208 C 92 216, 148 182, 206 168 S 304 124, 360 132",
  "M 40 168 C 96 172, 154 118, 212 108 S 306 92, 360 74",
  "M 40 228 C 92 192, 150 202, 206 186 S 302 158, 360 144",
];

const mobileSeriesPaths = [
  "M 34 170 C 70 144, 112 150, 150 122 S 224 102, 270 86",
  "M 34 188 C 72 196, 114 168, 154 154 S 226 118, 270 126",
  "M 34 150 C 74 156, 118 116, 158 102 S 228 88, 270 72",
  "M 34 206 C 72 176, 116 184, 156 170 S 226 144, 270 136",
];

export default function LineChartSkeleton({
  width = "100%",
  height = 500,
  tone = "soft",
  className = "",
  style,
  seriesCount = 3,
  showLegend = true,
  showAxes = true,
}: LineChartSkeletonProps): JSX.Element {
  const numericHeight = typeof height === "number" ? height : 500;
  const isCompact = numericHeight <= 320;
  const viewWidth = isCompact ? 300 : 400;
  const viewHeight = isCompact ? 220 : 280;
  const plotLeft = isCompact ? 34 : 40;
  const plotRight = isCompact ? 270 : 360;
  const plotTop = isCompact ? 20 : 24;
  const plotBottom = isCompact ? 190 : 232;
  const classes = [
    "domino-chart-skeleton",
    "domino-chart-skeleton--line",
    `domino-skeleton--tone-${tone}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const wrapperStyle: CSSProperties = {
    width: toCssDimension(width),
    height: toCssDimension(height),
    ...style,
  };

  const paths = (isCompact ? mobileSeriesPaths : desktopSeriesPaths).slice(
    0,
    Math.max(1, Math.min(seriesCount, 4)),
  );

  return (
    <div aria-hidden="true" className={classes} style={wrapperStyle}>
      {showLegend ? (
        <div className="domino-chart-skeleton__legend domino-chart-skeleton__legend--top">
          {Array.from({ length: Math.min(seriesCount, 4) }).map((_, index) => (
            <div key={`top-legend-${index}`} className="domino-chart-skeleton__legend-chip">
              <span className="domino-chart-skeleton__legend-bullet" />
              <span
                className="domino-chart-skeleton__legend-label"
                style={{ width: `${44 + (index % 3) * 10}%` }}
              />
            </div>
          ))}
        </div>
      ) : null}

      <div className="domino-chart-skeleton__canvas">
        <svg
          className="domino-chart-skeleton__svg"
          viewBox={`0 0 ${viewWidth} ${viewHeight}`}
          preserveAspectRatio="none"
        >
          {showAxes ? (
            <>
              <line
                x1={plotLeft}
                y1={plotBottom}
                x2={plotRight}
                y2={plotBottom}
                className="domino-chart-skeleton__axis"
              />
              <line
                x1={plotLeft}
                y1={plotTop}
                x2={plotLeft}
                y2={plotBottom}
                className="domino-chart-skeleton__axis"
              />
            </>
          ) : null}

          {Array.from({ length: 4 }).map((_, index) => {
            const y = plotTop + ((plotBottom - plotTop) / 4) * index;
            return (
              <line
                key={`grid-${index}`}
                x1={plotLeft}
                y1={y}
                x2={plotRight}
                y2={y}
                className="domino-chart-skeleton__grid"
              />
            );
          })}

          {Array.from({ length: 6 }).map((_, index) => {
            const x = plotLeft + ((plotRight - plotLeft) / 5) * index;
            return (
              <line
                key={`tick-${index}`}
                x1={x}
                y1={plotBottom}
                x2={x}
                y2={plotBottom + (isCompact ? 8 : 10)}
                className="domino-chart-skeleton__grid"
              />
            );
          })}

          {paths.map((path, index) => (
            <path
              key={`series-${index}`}
              d={path}
              className={`domino-chart-skeleton__line-path domino-chart-skeleton__line-path--${index % 3}`}
            />
          ))}

          {paths.slice(0, 1).map((path, index) => (
            <path
              key={`area-${index}`}
              d={`${path} L ${plotRight} ${plotBottom} L ${plotLeft} ${plotBottom} Z`}
              className="domino-chart-skeleton__area"
            />
          ))}
        </svg>
      </div>
    </div>
  );
}
